import React, { useState, useEffect } from 'react';

import { useEscapeToClose } from '../hooks/useEscapeToClose';
const PARENTAL_MEDICAL_CONDITIONS = {
  "Cardiovascular": [
    "Premature coronary artery disease / heart attack (≤55 M, ≤65 F)",
    "Stroke / TIA",
    "Aortic aneurysm / dissection (thoracic or abdominal)",
    "Cardiomyopathy (dilated, hypertrophic, restrictive)",
    "Sudden cardiac death / unexplained early death",
    "Arrhythmias (atrial fibrillation; inherited channelopathies: Long-QT, Brugada, CPVT)",
    "Congenital heart disease",
    "Hyperlipidemia (familial hypercholesterolemia)",
    "Hypertension (familial tendency)",
    "Peripheral arterial disease",
    "Valvular disease (bicuspid aortic valve)"
  ],
  "Endocrine & Metabolic": [
    "Diabetes (Type 2; Type 1 also has familial/autoimmune clustering)",
    "Thyroid disease (Hashimoto's, Graves', nodules/cancer)",
    "Osteoporosis / osteopenia",
    "Gout / hyperuricemia",
    "Pituitary/adrenal/parathyroid disorders",
    "Obesity / metabolic syndrome (familial tendency)"
  ],
  "Hematology & Clotting": [
    "Venous thromboembolism (DVT/PE), especially with known thrombophilia: Factor V Leiden, Prothrombin G20210A, Protein C/S or Antithrombin deficiency, Antiphospholipid syndrome",
    "Hemoglobinopathies: Sickle cell disease/trait, thalassemia",
    "Bleeding tendency: von Willebrand disease, platelet disorders, other coagulopathies",
    "Anemia (if familial pattern)",
    "Blood cancers: leukemia, lymphoma, myeloma, myelodysplastic syndromes",
    "Polycythemia vera / essential thrombocythemia"
  ],
  "Oncology (Cancer)": [
    "Breast cancer",
    "Ovarian cancer",
    "Colorectal cancer",
    "Prostate cancer",
    "Lung cancer",
    "Pancreatic cancer",
    "Stomach cancer",
    "Kidney cancer",
    "Bladder cancer",
    "Uterine/endometrial cancer",
    "Cervical cancer",
    "Liver cancer",
    "Brain tumors",
    "Melanoma",
    "Other skin cancers (basal/squamous cell)",
    "Thyroid cancer",
    "Head/neck cancers",
    "Sarcomas",
    "Multiple cancers in same person",
    "Cancer at young age (<50)"
  ],
  "Neurologic & Psychiatric": [
    "Alzheimer's disease / dementia",
    "Parkinson's disease",
    "Huntington's disease",
    "Multiple sclerosis",
    "ALS (Lou Gehrig's disease)",
    "Epilepsy / seizure disorder",
    "Migraine (familial pattern)",
    "Stroke at young age",
    "Neuropathy (inherited forms)",
    "Muscular dystrophy",
    "Depression (familial pattern)",
    "Bipolar disorder",
    "Schizophrenia",
    "Anxiety disorders (familial pattern)",
    "Suicide / suicide attempts",
    "Substance abuse / addiction"
  ],
  "Autoimmune / Inflammatory": [
    "Rheumatoid arthritis, lupus (SLE), Sjögren's",
    "Psoriasis / psoriatic arthritis",
    "Ankylosing spondylitis / axial spondyloarthritis (HLA-B27)",
    "Inflammatory bowel disease (Crohn's, ulcerative colitis)",
    "Celiac disease",
    "Vasculitis (e.g., GCA/PMR), scleroderma",
    "Sarcoidosis"
  ],
  "Respiratory & Hepatic": [
    "Alpha-1 antitrypsin deficiency (COPD/liver disease)",
    "Asthma (familial tendency)",
    "Pulmonary fibrosis / interstitial lung disease (familial forms)",
    "Primary biliary cholangitis, autoimmune hepatitis",
    "Wilson disease"
  ],
  "Gastrointestinal": [
    "Colon polyps (advanced/≥3 or any before age 50)",
    "Pancreatitis (familial forms or with high triglycerides)",
    "Gallstones (familial tendency but lower priority unless early/recurrent)"
  ],
  "Renal / Genitourinary & Reproductive": [
    "Polycystic kidney disease (ADPKD)",
    "Chronic kidney disease of unknown cause in a parent",
    "Recurrent kidney stones (familial calcium/uric acid stone disease)",
    "Endometriosis (familial tendency)",
    "Uterine fibroids (familial tendency)",
    "Infertility (noting specific known genetic causes if present)"
  ],
  "Ophthalmology & Dermatology": [
    "Glaucoma",
    "Age-related macular degeneration",
    "Melanoma",
    "Severe eczema/atopy (familial tendency)",
    "Vitiligo (autoimmune clustering)"
  ],
  "Musculoskeletal & Connective Tissue": [
    "Early/fragility fractures / osteoporosis",
    "Marfan syndrome, Ehlers-Danlos, other connective-tissue disorders",
    "Scoliosis requiring surgery (familial patterns)"
  ]
};

const ParentalHistoryModal = ({ isOpen, onClose, onSave, existingData }) => {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [fatherInfo, setFatherInfo] = useState({
    isDeceased: false,
    dateDeceased: ''
  });
  const [motherInfo, setMotherInfo] = useState({
    isDeceased: false,
    dateDeceased: ''
  });
  const [parentalConditions, setParentalConditions] = useState({});

  useEffect(() => {
    if (existingData) {
      // FIXED: Use snake_case field names that match backend
      setFatherInfo(existingData.father_info || { isDeceased: false, dateDeceased: '' });
      setMotherInfo(existingData.mother_info || { isDeceased: false, dateDeceased: '' });
      setParentalConditions(existingData.parental_conditions || {});
    }
  }, [existingData]);

  const handleFatherDeceasedChange = (checked) => {
    setFatherInfo(prev => ({
      ...prev,
      isDeceased: checked,
      dateDeceased: checked ? prev.dateDeceased : ''
    }));
  };

  const handleMotherDeceasedChange = (checked) => {
    setMotherInfo(prev => ({
      ...prev,
      isDeceased: checked,
      dateDeceased: checked ? prev.dateDeceased : ''
    }));
  };

  const handleConditionToggle = (condition, parent) => {
    setParentalConditions(prev => ({
      ...prev,
      [condition]: {
        ...prev[condition],
        [parent]: !prev[condition]?.[parent]
      }
    }));
  };

  const handleSave = () => {
    const formData = {
      // FIXED: Send snake_case field names that backend expects
      father_info: fatherInfo,
      mother_info: motherInfo,
      parental_conditions: parentalConditions
    };
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content parental-history-modal">
        <div className="modal-header">
          <h2>Parental Medical History</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="instructions">
            Only enter information pertaining to blood relatives - (i.e. your blood-related Father and/or your blood-related Mother). If unknown leave blank.
          </div>

          <div className="parent-info-section">
            <div className="parent-info-row">
              <div className="parent-cell">
                <strong>Father</strong>
              </div>
              <div className="deceased-cell">
                <label>
                  <input
                    type="checkbox"
                    checked={fatherInfo.isDeceased}
                    onChange={(e) => handleFatherDeceasedChange(e.target.checked)}
                  />
                  Deceased
                </label>
                {fatherInfo.isDeceased && (
                  <input
                    type="date"
                    value={fatherInfo.dateDeceased}
                    onChange={(e) => setFatherInfo(prev => ({ ...prev, dateDeceased: e.target.value }))}
                    className="date-input"
                    placeholder="Date Deceased"
                  />
                )}
              </div>
              <div className="parent-cell">
                <strong>Mother</strong>
              </div>
              <div className="deceased-cell">
                <label>
                  <input
                    type="checkbox"
                    checked={motherInfo.isDeceased}
                    onChange={(e) => handleMotherDeceasedChange(e.target.checked)}
                  />
                  Deceased
                </label>
                {motherInfo.isDeceased && (
                  <input
                    type="date"
                    value={motherInfo.dateDeceased}
                    onChange={(e) => setMotherInfo(prev => ({ ...prev, dateDeceased: e.target.value }))}
                    className="date-input"
                    placeholder="Date Deceased"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="conditions-grid">
            <div className="grid-header">
              <div className="condition-column">Condition</div>
              <div className="parent-column">Father</div>
              <div className="parent-column">Mother</div>
            </div>

            {Object.entries(PARENTAL_MEDICAL_CONDITIONS).map(([category, conditions]) => (
              <div key={category} className="category-section">
                  <div className="category-header grid col-span-full">
                  <div className="category-title">{category}</div>
                </div>
                {conditions.map((condition, index) => (
                  <div key={`${category}-${index}`} className="condition-row">
                    <div className="condition-cell">
                      {condition}
                    </div>
                    <div className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={parentalConditions[condition]?.father || false}
                        onChange={() => handleConditionToggle(condition, 'father')}
                      />
                    </div>
                    <div className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={parentalConditions[condition]?.mother || false}
                        onChange={() => handleConditionToggle(condition, 'mother')}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button className="save-button" onClick={handleSave}>Save Parental History</button>
        </div>
      </div>
    </div>
  );
};

export default ParentalHistoryModal;